import { JSX, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LeftArrowIcon } from 'src/assets/icons/bonus-game/left-arrow.svg';
import { ReactComponent as CoinIcon } from 'src/assets/icons/bonus-game/coin.svg';
import { ReactComponent as RightArrowIcon } from 'src/assets/icons/bonus-game/right-arrow.svg';
import questIcon from 'src/assets/img/bonus-game/quest-icon.png';
import Loader from 'src/pages/Education/components/Loader';

import { ReactComponent as BuyIcon } from 'src/assets/icons/bonus-game/quests/buy-icon.svg';
import { ReactComponent as HaqabiLogo } from 'src/assets/icons/bonus-game/quests/haqabi-logo.svg';
import { ReactComponent as InviteIcon } from 'src/assets/icons/bonus-game/quests/invite-icon.svg';
import { ReactComponent as StakingIcon } from 'src/assets/icons/bonus-game/quests/try-staking.svg';
import { ReactComponent as TrySwapsIcon } from 'src/assets/icons/bonus-game/quests/try-swaps.svg';
import { ReactComponent as TryDappsIcon } from 'src/assets/icons/bonus-game/quests/try-dapps.svg';
import { ReactComponent as DepositIcon } from 'src/assets/icons/bonus-game/quests/deposit.svg';
import { ReactComponent as CheckIcon } from 'src/assets/icons/check.svg';
import { MobileHeader } from 'src/pages/BonusGame/components/Header';
import { TabsSwitcher } from 'src/pages/BonusGame/components/Tabs';
import {
  CompletionSection,
  CompletionText,
  Ellipse,
  IconContainer,
  NoAvailableQuestText,
  QuestInfo,
  QuestItem,
  QuestName,
  QuestsList,
  RewardAmount,
  RewardContainer,
  CompletedDate,
} from './styled';
import { PageWrapper } from '../../ui/common';
import { bonusGameApi } from 'src/api/bonus-game';
import { GetQuestsInfoResponseObject } from 'src/api/Api';
import { HorizontalView } from 'src/components/FlexViews';

interface QuestType extends GetQuestsInfoResponseObject {
  title: string;
  iconName: string;
  icon: JSX.Element;
  actionType: string;
  description: string;
}

const convertQuestsData = (quests: GetQuestsInfoResponseObject[]) => {
  const convertedQuests: QuestType[] = [];

  quests.forEach(quest => {
    switch (quest.questType) {
      case 'VISIT_HAQABI_WEBSITE':
        convertedQuests.push({
          ...quest,
          icon: <HaqabiLogo />,
          title: 'Visit Haqabi website',
          iconName: 'haqabiIcon',
          actionType: 'navigate_haqabi_webpage',
          description:
            'Visit the Haqabi website, explore its key features, and dive into the Education section to expand your knowledge.',
        });
        break;
      case 'BUY_CRYPTO':
        convertedQuests.push({
          ...quest,
          title: 'Buy crypto',
          icon: <BuyIcon />,
          iconName: 'buyIcon',
          actionType: 'navigate_buy',
          description:
            'Buy crypto in the Haqabi app and get ready to use it anytime you need.',
        });
        break;
      case 'TRY_DAPPS':
        convertedQuests.push({
          ...quest,
          title: 'Try DApps',
          icon: <TryDappsIcon />,
          iconName: 'dappsIcon',
          actionType: 'navigate_dapps',
          description:
            'Try exploring dApps to discover new ways to use and grow your crypto.',
        });
        break;
      case 'TRY_SWAPS':
        convertedQuests.push({
          ...quest,
          title: 'Try swaps',
          icon: <TrySwapsIcon />,
          iconName: 'swapIcon',
          actionType: 'navigate_swaps',
          description:
            'Use swaps to quickly exchange your crypto and stay flexible.',
        });
        break;
      case 'TRY_STAKING':
        convertedQuests.push({
          ...quest,
          title: 'Try Staking',
          icon: <StakingIcon />,
          iconName: 'stakingIcon',
          actionType: 'navigate_staking',
          description:
            'Use staking to grow your crypto while keeping it secure.',
        });
        break;
      case 'INVITE_ONE_FRIEND':
        convertedQuests.push({
          ...quest,
          title: 'Invite 1 friend',
          icon: <InviteIcon />,
          iconName: 'inviteIcon',
          actionType: 'invite_friend',
          description: 'Invite friends using your promo code',
        });
        break;
      case 'INVITE_FIVE_FRIEND':
        convertedQuests.push({
          ...quest,
          title: 'Invite 5 friends',
          icon: <InviteIcon />,
          iconName: 'inviteIcon',
          actionType: 'invite_friend',
          description: 'Invite friends using your promo code',
        });
        break;
      case 'INVITE_TEN_FRIEND':
        convertedQuests.push({
          ...quest,
          title: 'Invite 10 friends',
          icon: <InviteIcon />,
          iconName: 'inviteIcon',
          actionType: 'invite_friend',
          description: 'Invite friends using your promo code',
        });
        break;
      case 'DEPOSIT_100':
        convertedQuests.push({
          ...quest,
          title: 'Deposit $100',
          icon: <DepositIcon />,
          iconName: 'depositIcon',
          actionType: 'deposit',
          description:
            'Reach an equivalent of 100 USDT in your wallet balance and receive a reward!',
        });
        break;
      case 'DEPOSIT_200':
        convertedQuests.push({
          ...quest,
          title: 'Deposit $200',
          icon: <DepositIcon />,
          iconName: 'depositIcon',
          actionType: 'deposit',
          description:
            'Reach an equivalent of 200 USDT in your wallet balance and receive a reward!',
        });
        break;
      case 'DEPOSIT_500':
        convertedQuests.push({
          ...quest,
          title: 'Deposit $500',
          icon: <DepositIcon />,
          iconName: 'depositIcon',
          actionType: 'deposit',
          description:
            'Reach an equivalent of 500 USDT in your wallet balance and receive a reward!',
        });
        break;
      case 'DEPOSIT_1000':
        convertedQuests.push({
          ...quest,
          title: 'Deposit $1000',
          icon: <DepositIcon />,
          iconName: 'depositIcon',
          actionType: 'deposit',
          description:
            'Reach an equivalent of 1000 USDT in your wallet balance and receive a reward!',
        });
        break;
    }
  });

  return convertedQuests;
};

const enum Tab {
  Available = 'AVAILABLE',
  Completed = 'COMPLETED',
}

function formatDate(dateString: any) {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export const Quests = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(Tab.Available);
  const [isLoading, setIsLoading] = useState(true);
  const [availableQuests, setAvailableQuests] = useState<
    GetQuestsInfoResponseObject[]
  >([]);
  const [completedQuests, setCompletedQuests] = useState<
    GetQuestsInfoResponseObject[]
  >([]);
  const [promoCode, setPromoCode] = useState('');

  useEffect(() => {
    const fetchQuests = async () => {
      try {
        setIsLoading(true);
        const availableQuestInfoReq = bonusGameApi.getQuestsInfo({
          filter: Tab.Available,
        });
        const completedQuestInfoReq = bonusGameApi.getQuestsInfo({
          filter: Tab.Completed,
        });
        const [availableQuestInfoRes, completedQuestInfoRes] =
          await Promise.all([availableQuestInfoReq, completedQuestInfoReq]);
        const promocodeRes = await bonusGameApi.generateInviteCode();
        setPromoCode(promocodeRes.result.promoCode);
        setAvailableQuests(availableQuestInfoRes.result);
        setCompletedQuests(completedQuestInfoRes.result);
      } catch (error) {
        console.error('Failed to fetch quests:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuests();
  }, []);

  const handleQuestClick = (quest: QuestType) => {
    if (activeTab === Tab.Completed) return;
    const showPromocode = quest.iconName === 'inviteIcon';

    navigate('/bonus-game/quests-details', {
      state: {
        ...quest,
        icon: undefined,
        promocode: showPromocode ? promoCode : undefined,
      },
    });
  };

  const quests = useMemo(
    () =>
      activeTab === Tab.Available
        ? convertQuestsData(availableQuests)
        : convertQuestsData(completedQuests),
    [activeTab, availableQuests, completedQuests],
  );

  console.log('quests', quests);

  if (isLoading) return <Loader small />;

  return (
    <PageWrapper>
      <MobileHeader title="Quests" />
      <div style={{ position: 'relative', alignSelf: 'center' }}>
        <img style={{ width: '100%' }} src={questIcon} />
        <Ellipse />
      </div>
      <CompletionSection>
        <CompletionText>Complete quests and win rewards!</CompletionText>
      </CompletionSection>

      <TabsSwitcher
        tabs={[
          {
            value: Tab.Available,
            label: `Available quests ${availableQuests.length}`,
          },
          {
            value: Tab.Completed,
            label: `Completed ${completedQuests.length}`,
          },
        ]}
        currentTabValue={activeTab}
        onChange={val => setActiveTab(val as Tab)}
      />

      <QuestsList>
        {quests.length ? (
          quests.map(quest => (
            <QuestItem
              key={quest.questType}
              onClick={() => handleQuestClick(quest)}>
              <QuestInfo>
                <IconContainer>{quest.icon}</IconContainer>
                <div
                  style={{
                    height: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}>
                  <QuestName>{quest.title}</QuestName>
                  <HorizontalView>
                    <RewardAmount>
                      <CoinIcon width={12} height={12} />
                      {quest.reward}
                    </RewardAmount>
                    {activeTab === Tab.Completed && (
                      <CompletedDate>
                        {formatDate(quest.completedAt)}
                      </CompletedDate>
                    )}
                  </HorizontalView>
                </div>
              </QuestInfo>
              {activeTab === Tab.Completed ? (
                <CheckIcon width={24} height={24} />
              ) : (
                <RewardContainer>
                  <RightArrowIcon color="rgba(82, 90, 103, 1)" />
                </RewardContainer>
              )}
            </QuestItem>
          ))
        ) : (
          <NoAvailableQuestText>
            {' '}
            {activeTab === Tab.Available
              ? 'No available quests yet'
              : 'No completed quests yet'}
          </NoAvailableQuestText>
        )}
      </QuestsList>
    </PageWrapper>
  );
};
